import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { apiFetch } from "../api/client";

export type Role = "admin" | "manager" | "operator";
export type Lang = "ko" | "en" | "vi";

export type CurrentUser = {
  id: number;
  username: string;
  name: string;
  role: Role;
  preferredLang: Lang;
  mustChangePassword: boolean;
};

type AuthState = {
  user: CurrentUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { user } = await apiFetch<{ user: CurrentUser }>("/auth/me");
      setUser(user);
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    void (async () => {
      await refresh();
      setLoading(false);
    })();
  }, []);

  const login = async (username: string, password: string) => {
    const { user } = await apiFetch<{ user: CurrentUser }>("/auth/login", {
      method: "POST",
      json: { username, password },
    });
    setUser(user);
  };

  const logout = async () => {
    await apiFetch("/auth/logout", { method: "POST" });
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh }}>{children}</AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
